import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { DialogContext } from '../store/context/DialogContext';

const BROWSER_WARNING_MESSAGE = 'Your latest changes may not be saved yet. If you leave now, they could be lost.';

const isConfirmResult = (result) => result === true || result === 2 || result?.confirmed === true;

const useRouteBlocker = ({ when, onBlock }) => {
    const navigationContext = useContext(UNSAFE_NavigationContext);
    const navigator = navigationContext?.navigator;
    const [blockerKey, setBlockerKey] = useState(0);

    useEffect(() => {
        if (!when || typeof navigator?.block !== 'function') {
            return undefined;
        }

        const unblock = navigator.block((tx) => {
            const autoUnblockingTx = {
                ...tx,
                retry() {
                    setBlockerKey((current) => current + 1);
                    unblock();
                    tx.retry();
                },
            };

            onBlock(autoUnblockingTx);
        });

        return unblock;
    }, [blockerKey, navigator, onBlock, when]);
};

export default function useUnsavedChangesWarning({
    enabled = true,
    enableBeforeUnload = false,
    when = false,
    isSaving = false,
    pendingChangesCount = 0,
    title = 'Leave this page?',
    confirmLabel = 'Leave page',
    cancelLabel = 'Stay here',
}) {
    const { showConfirm } = useContext(DialogContext);
    const shouldWarnRef = useRef(enabled && when);
    const hasHumanEditedRef = useRef(false);
    const dialogOpenRef = useRef(false);

    useEffect(() => {
        shouldWarnRef.current = enabled && when;

        if (!enabled || !when) {
            hasHumanEditedRef.current = false;
        }
    }, [enabled, when]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        const markHumanEdit = (event) => {
            const target = event?.target;

            if (!target || typeof target !== 'object') {
                return;
            }

            const isSupportedField = target instanceof HTMLInputElement
                || target instanceof HTMLTextAreaElement
                || target instanceof HTMLSelectElement
                || target.isContentEditable === true;

            if (isSupportedField) {
                hasHumanEditedRef.current = true;
            }
        };

        window.addEventListener('input', markHumanEdit, true);
        window.addEventListener('change', markHumanEdit, true);

        return () => {
            window.removeEventListener('input', markHumanEdit, true);
            window.removeEventListener('change', markHumanEdit, true);
        };
    }, [enabled]);

    useEffect(() => {
        if (!enableBeforeUnload || !enabled || !when) {
            return undefined;
        }

        const handleBeforeUnload = (event) => {
            if (!shouldWarnRef.current || !hasHumanEditedRef.current) {
                return undefined;
            }

            event.preventDefault();
            event.returnValue = '';
            return '';
            // event.returnValue = BROWSER_WARNING_MESSAGE;
            // return BROWSER_WARNING_MESSAGE;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [enableBeforeUnload, enabled, when]);

    const getDescription = useCallback(() => {
        if (isSaving) {
            return 'We are still saving your latest changes. If you leave now, they may be lost.';
        }

        if (pendingChangesCount > 1) {
            return `You have ${pendingChangesCount} unsaved changes. If you leave now, they may be lost.`;
        }

        return 'You have unsaved changes. If you leave now, they may be lost.';
    }, [isSaving, pendingChangesCount]);

    const handleBlockedNavigation = useCallback(async (transition) => {
        if (!shouldWarnRef.current || !hasHumanEditedRef.current) {
            transition.retry();
            return;
        }

        if (dialogOpenRef.current) {
            return;
        }

        dialogOpenRef.current = true;

        try {
            const result = await showConfirm({
                title,
                description: getDescription(),
                proceed: confirmLabel,
                reject: cancelLabel,
                testId: 'unsaved-changes-confirm',
            });

            if (isConfirmResult(result)) {
                transition.retry();
            }
        } finally {
            dialogOpenRef.current = false;
        }
    }, [cancelLabel, confirmLabel, getDescription, showConfirm, title]);

    useRouteBlocker({
        when: enabled && when,
        onBlock: handleBlockedNavigation,
    });
}
